import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import dayjs from "dayjs";
import { useServiceCenters } from "../../hooks/react-query/useServiceCenters";
import {
  formatLocationWithSicCodes,
  createSicCodeMap,
  getSicCodeFromId,
} from "./equipmentDropdown.utils";
import type {
  EquipmentOptionDisplay,
  EquipmentDropdownProps,
} from "./EquipmentDropdown.types";
import { AssignmentTypeIcon } from "./AssignmentTypeIcon";
import { LoadSummaryDisplay } from "./LoadSummaryDisplay";
import PlannedShipmentStatusTag from "./StatusTag/PlannedShipmentStatusTag";

export const EquipmentDropdown = React.memo(
  ({
    value,
    onChange,
    options,
    label = "Select Equipment",
    disabled = false,
    getOptionDisabled,
    disableClearable = false,
    error = false,
    helperText,
  }: EquipmentDropdownProps) => {
    const serviceCentersQuery = useServiceCenters("All");
    const theme = useTheme();
    const sicCodeMap = useMemo(
      () => createSicCodeMap(serviceCentersQuery.data ?? []),
      [serviceCentersQuery.data]
    );

    const transformedOptions = useMemo(
      () =>
        options.map((option) => ({
          ...option,
          locationDisplay: formatLocationWithSicCodes(option.rawData, (sicId) =>
            getSicCodeFromId(sicId, sicCodeMap)
          ),
        })),
      [options, sicCodeMap]
    );

    const groupBy = (option: EquipmentOptionDisplay) =>
      option.isAvailable
        ? "Available to reposition"
        : "Unavailable to reposition";

    const getOptionLabel = (option: EquipmentOptionDisplay) => option.refNumber;

    const isOptionEqualToValue = (
      option: EquipmentOptionDisplay,
      value: EquipmentOptionDisplay
    ) => option.id === value.id;

    return (
      <Autocomplete
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        options={transformedOptions}
        groupBy={groupBy}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        getOptionDisabled={getOptionDisabled}
        disableClearable={disableClearable}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: option.isAvailable
                        ? theme.palette.text.primary
                        : theme.palette.grey[500],
                      fontSize: 16,
                    }}
                  >
                    {option.refNumber}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  {!option.isAvailable &&
                  option.rawData.assignedTo?.linehaulInfo &&
                  (option.rawData.assignedTo.linehaulInfo.originSicId ||
                    option.rawData.assignedTo.linehaulInfo.destinationSicId) ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        sx={{
                          color: theme.palette.grey[500],
                        }}
                      >
                        {option.rawData.assignedTo.linehaulInfo.originSicId
                          ? getSicCodeFromId(
                              option.rawData.assignedTo.linehaulInfo
                                .originSicId,
                              sicCodeMap
                            )
                          : null}
                      </Typography>
                      <ArrowForward
                        sx={{
                          fontSize: "16px",
                          color: theme.palette.grey[500],
                        }}
                      />
                      <Typography
                        sx={{
                          color: theme.palette.grey[500],
                        }}
                      >
                        {option.rawData.assignedTo.linehaulInfo.destinationSicId
                          ? getSicCodeFromId(
                              option.rawData.assignedTo.linehaulInfo
                                .destinationSicId,
                              sicCodeMap
                            )
                          : null}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        color: theme.palette.grey[500],
                      }}
                    >
                      {getSicCodeFromId(option.rawData.homeSicId, sicCodeMap)}
                    </Typography>
                  )}
                  <PlannedShipmentStatusTag
                    label={option.statusName}
                    isPlannedForToday={false}
                    selectedDate={dayjs().toISOString()}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {option.rawData.assignedTo && (
                    <AssignmentTypeIcon
                      assignmentType={option.rawData.assignedTo.type}
                    />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "143%",
                      letterSpacing: "0.17px",
                      color: theme.palette.grey[500],
                    }}
                  >
                    {option.rawData.assignedTo?.name}
                  </Typography>
                </Box>
                {!option.isAvailable && (
                  <LoadSummaryDisplay
                    loadSummary={option.rawData.loadSummary}
                  />
                )}
              </Box>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
          />
        )}
      />
    );
  }
);

EquipmentDropdown.displayName = "EquipmentDropdown";

// Re-export types for convenience
export type { EquipmentOptionDisplay, EquipmentDropdownProps };
